// The JS twin of src/Netcountable.App/E2EE/SodiumEnvelope.cs (NC1 envelope)
// plus the enrollment fingerprint and device-list signing-bytes helpers from
// E2EEManager.cs / E2EEModels.cs. Byte-compatibility with C# is the contract —
// verify-vectors.js enforces it.
//
// Every function takes the ready `sodium` instance (browser: window.sodium
// after the two vendor <script> tags; Node: `await import('libsodium-wrappers-sumo')`).

import { canonicalize, canonicalizeBytes } from './jcs.js';

const MAGIC = new Uint8Array([0x4e, 0x43, 0x31]); // "NC1"
const NONCE_BYTES = 24;  // crypto_aead_xchacha20poly1305_ietf_NPUBBYTES
const SIG_BYTES = 64;    // crypto_sign_BYTES
const HASH_BYTES = 32;   // BLAKE2b-256
const MAX_HEADER_BYTES = 64 * 1024;

// ---- base64: standard alphabet WITH padding, matching C# Convert.To/FromBase64String ----
export function b64(sodium, bytes) {
  return sodium.to_base64(bytes, sodium.base64_variants.ORIGINAL);
}
export function ub64(sodium, str) {
  return sodium.from_base64(str, sodium.base64_variants.ORIGINAL);
}

export function concatBytes(...arrays) {
  const total = arrays.reduce((n, a) => n + a.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const a of arrays) { out.set(a, off); off += a.length; }
  return out;
}

/** aad = { accountId, recordId, deviceId, epoch, field } — epoch must be an integer. */
export function aadBytes(aad) {
  return canonicalizeBytes({
    accountId: aad.accountId,
    recordId: aad.recordId,
    deviceId: aad.deviceId,
    epoch: aad.epoch,
    field: aad.field,
  });
}

/** BLAKE2b-32(ciphertext ‖ canon(aad)) — the signed digest. */
function signingDigest(sodium, ciphertext, aadB) {
  return sodium.crypto_generichash(HASH_BYTES, concatBytes(ciphertext, aadB));
}

/** Encrypt + sign into a binary NC1 envelope (Uint8Array). */
export function sealEnvelope(sodium, plaintext, aad, adk, devSignSk) {
  const aadB = aadBytes(aad);
  const nonce = sodium.randombytes_buf(NONCE_BYTES);
  const ciphertext = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(plaintext, aadB, null, nonce, adk);
  const sig = sodium.crypto_sign_detached(signingDigest(sodium, ciphertext, aadB), devSignSk);

  const header = {
    v: 1,
    aad: { accountId: aad.accountId, recordId: aad.recordId, deviceId: aad.deviceId, epoch: aad.epoch, field: aad.field },
    nonce: b64(sodium, nonce),
    sig: b64(sodium, sig),
  };
  const headerBytes = new TextEncoder().encode(canonicalize(header));

  const lenBuf = new Uint8Array(4);
  new DataView(lenBuf.buffer).setUint32(0, headerBytes.length, /* littleEndian */ true);

  return concatBytes(MAGIC, lenBuf, headerBytes, ciphertext);
}

/** Seal a UTF-8 string payload, return standard base64 of the envelope. */
export function sealEnvelopeString(sodium, plaintextStr, aad, adk, devSignSk) {
  return b64(sodium, sealEnvelope(sodium, new TextEncoder().encode(plaintextStr), aad, adk, devSignSk));
}

/** Split an envelope into { v, aad, nonce, sig, ciphertext }. Throws on malformed input. */
export function parseEnvelope(sodium, envelope) {
  if (envelope.length < MAGIC.length + 4) throw new Error('Envelope too short.');
  if (envelope[0] !== MAGIC[0] || envelope[1] !== MAGIC[1] || envelope[2] !== MAGIC[2]) {
    throw new Error('Envelope magic mismatch — not an NC1 envelope.');
  }
  const headerLen = new DataView(envelope.buffer, envelope.byteOffset + MAGIC.length, 4).getUint32(0, true);
  if (headerLen > MAX_HEADER_BYTES || MAGIC.length + 4 + headerLen > envelope.length) {
    throw new Error('Envelope header length out of range.');
  }
  const headerJson = new TextDecoder().decode(envelope.subarray(MAGIC.length + 4, MAGIC.length + 4 + headerLen));
  const header = JSON.parse(headerJson);
  if (header.v !== 1) throw new Error(`Unsupported envelope version ${header.v}.`);

  const nonce = ub64(sodium, header.nonce);
  const sig = ub64(sodium, header.sig);
  if (nonce.length !== NONCE_BYTES) throw new Error('Envelope nonce has wrong length.');
  if (sig.length !== SIG_BYTES) throw new Error('Envelope signature has wrong length.');

  return {
    v: header.v,
    aad: header.aad,
    nonce,
    sig,
    ciphertext: envelope.subarray(MAGIC.length + 4 + headerLen),
  };
}

export function parseEnvelopeString(sodium, envelopeB64) {
  return parseEnvelope(sodium, ub64(sodium, envelopeB64));
}

/** Provenance check — no decryption, no ADK needed. */
export function verifyEnvelope(sodium, parsed, devSignPk) {
  return sodium.crypto_sign_verify_detached(
    parsed.sig, signingDigest(sodium, parsed.ciphertext, aadBytes(parsed.aad)), devSignPk);
}

/** AEAD decrypt. Throws on tag/AAD mismatch. Returns Uint8Array. */
export function decryptEnvelope(sodium, parsed, adk) {
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null, parsed.ciphertext, aadBytes(parsed.aad), parsed.nonce, adk);
}

/**
 * Enrollment fingerprint:
 * hex(first 8 bytes of BLAKE2b-16(transfer_pk ‖ dev_sign_pk ‖ dev_enc_pk)).
 * (BLAKE2b-16 then truncate, because crypto_generichash_BYTES_MIN is 16 —
 * same rule as C# E2EEManager.ComputeFingerprint. dev_enc_pk is bound in so a
 * relay can't swap the device's encryption key to intercept a future
 * identity-rotation delivery.)
 */
export function computeFingerprint(sodium, transferPk, devSignPk, devEncPk) {
  const hash = sodium.crypto_generichash(16, concatBytes(transferPk, devSignPk, devEncPk));
  return Array.from(hash.subarray(0, 8), (x) => x.toString(16).padStart(2, '0')).join('');
}

/** XXXX-XXXX-XXXX-XXXX display form. */
export function fingerprintDisplay(fingerprint) {
  return fingerprint.toUpperCase().match(/.{4}/g).join('-');
}

/**
 * §8.3 read-and-verify for one parsed envelope:
 *   1. resolve the author device's signing pk from the (already
 *      identity-verified) device list — unknown device = untrusted
 *   2. verify the device signature over BLAKE2b(ciphertext ‖ canon(aad))
 *   3. AEAD-decrypt with the ADK for the envelope's epoch
 * Returns { ok, plaintext?, error? } — never throws.
 *
 * @param ctx { devicePks: Map<deviceId, Uint8Array>, adks: Map<epochNumber, Uint8Array> }
 */
export function openParsed(sodium, parsed, ctx) {
  const devPk = ctx.devicePks.get(parsed.aad.deviceId);
  if (!devPk) return { ok: false, error: `unknown device ${parsed.aad.deviceId} (not on the signed device list)` };
  if (!verifyEnvelope(sodium, parsed, devPk)) return { ok: false, error: 'BAD PROVENANCE SIGNATURE' };
  const adk = ctx.adks.get(parsed.aad.epoch);
  if (!adk) return { ok: false, error: `no ADK for epoch ${parsed.aad.epoch}` };
  try {
    // Tombstoned author: historical records verify, but surface it — new
    // records from a revoked device are a red flag for the caller/UI.
    const revoked = ctx.revokedDevices?.has(parsed.aad.deviceId) ?? false;
    return { ok: true, plaintext: decryptEnvelope(sodium, parsed, adk), revoked };
  } catch {
    return { ok: false, error: 'AEAD DECRYPT FAILED (tag/AAD mismatch)' };
  }
}

/**
 * The exact bytes the identity key signs for a device list:
 * canon({ devices: [{addedAt, devEncPk, devSignPk, deviceId}], userId, version }).
 * Mirror of C# SignedDeviceList.SignedBytes() / DeviceListEntry.ToJsonNode().
 */
export function deviceListSignedBytes(list) {
  return canonicalizeBytes({
    userId: list.userId,
    version: list.version,
    devices: list.devices.map((d) => deviceEntryForSigning(d)),
  });
}

/** Canonical shape of one device-list entry (shared by signing + re-signing paths). */
export function deviceEntryForSigning(d) {
  return {
    deviceId: d.deviceId,
    devSignPk: d.devSignPk,
    devEncPk: d.devEncPk,
    addedAt: d.addedAt,
    // Tombstone for revoked devices — omitted entirely when absent so the
    // canonical bytes match C# DeviceListEntry.ToJsonNode().
    ...(d.revokedAt != null ? { revokedAt: d.revokedAt } : {}),
  };
}
